import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Avatar,
  Snackbar,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack,
  Business,
  Store,
  People,
  Settings,
  Security,
  Notifications,
  Help,
  Logout,
  Edit,
  LocationOn,
  AccessTime,
  Fingerprint,
  CalendarToday,
  Report,
  VpnKey,
  Backup,
  CloudUpload,
  SystemUpdate,
  Info,
  Storage,
  Delete,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants';
import settingsService, { StorageCleanupSettings, StorageCleanupResult } from '../../services/settingsService';
import ProfileAvatar from '../../components/common/ProfileAvatar';

interface SettingsItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action?: () => void;
  badge?: string;
  disabled?: boolean;
}

interface CompanySettings {
  companyName: string;
  companyCode: string;
  totalBranches: number;
  totalDepartments: number;
  totalEmployees: number;
  inactiveEmployees: number;
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    companyName: 'TRIVENI TRADERS',
    companyCode: 'TRIVENI001',
    totalBranches: 0,
    totalDepartments: 0,
    totalEmployees: 0,
    inactiveEmployees: 0,
  });

  // Dialog states
  const [editCompanyDialog, setEditCompanyDialog] = useState(false);
  const [logoutDialog, setLogoutDialog] = useState(false);
  const [storageCleanupDialog, setStorageCleanupDialog] = useState(false);
  const [storageCleanupSettings, setStorageCleanupSettings] = useState<StorageCleanupSettings | null>(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<StorageCleanupResult | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [companyForm, setCompanyForm] = useState({
    name: companySettings.companyName,
    code: companySettings.companyCode,
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    if (storageCleanupDialog) {
      fetchStorageCleanupSettings();
    }
  }, [storageCleanupDialog]);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getCompanySettings();
      setCompanySettings(response);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      // Fallback to mock data
      setCompanySettings({
        companyName: 'TRIVENI TRADERS',
        companyCode: 'TRIVENI001',
        totalBranches: 5,
        totalDepartments: 8,
        totalEmployees: 150,
        inactiveEmployees: 12,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = () => {
    setCompanyForm({
      name: companySettings.companyName,
      code: companySettings.companyCode,
    });
    setEditCompanyDialog(true);
  };

  const handleSaveCompany = async () => {
    try {
      await settingsService.updateCompanySettings({
        companyName: companyForm.name,
        companyCode: companyForm.code,
      });
      
      setCompanySettings(prev => ({
        ...prev,
        companyName: companyForm.name,
        companyCode: companyForm.code,
      }));
      setEditCompanyDialog(false);
    } catch (error) {
      console.error('Failed to update company settings:', error);
    }
  };

  const handleLogout = async () => {
    setLogoutDialog(false);
    await logout();
  };

  const fetchStorageCleanupSettings = async () => {
    try {
      const settings = await settingsService.getStorageCleanupSettings();
      setStorageCleanupSettings(settings);
    } catch (error) {
      console.error('Failed to fetch storage cleanup settings:', error);
      setSnackbar({ open: true, message: 'Failed to load storage cleanup settings', severity: 'error' });
    }
  };

  const handleRunCleanup = async () => {
    try {
      setCleanupRunning(true);
      setCleanupResult(null);
      const result = await settingsService.runStorageCleanup();
      setCleanupResult(result);
      setSnackbar({ open: true, message: 'Photo cleanup completed successfully', severity: 'success' });
      // Refresh settings to get updated last cleanup run time
      await fetchStorageCleanupSettings();
    } catch (error: any) {
      console.error('Failed to run cleanup:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to run photo cleanup', severity: 'error' });
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleOpenStorageCleanup = () => {
    setStorageCleanupDialog(true);
    setCleanupResult(null);
  };

  const companySettingsItems: SettingsItem[] = [
    {
      id: 'company-details',
      title: companySettings.companyName,
      description: 'Edit company details',
      icon: <Business />,
      action: handleEditCompany,
    },
    {
      id: 'branches',
      title: 'My Branches',
      description: `Manage ${companySettings.totalBranches} branches`,
      icon: <Store />,
      action: () => navigate(ROUTES.STORES),
    },
    {
      id: 'departments',
      title: 'My Departments',
      description: `Manage ${companySettings.totalDepartments} departments`,
      icon: <Business />,
      action: () => navigate(ROUTES.CLUSTERS),
    },
    {
      id: 'company-reports',
      title: 'Company Reports',
      description: 'Generate different types of reports for whole company',
      icon: <Report />,
      action: () => navigate('/reports'),
    },
  ];

  const teamSettingsItems: SettingsItem[] = [
    {
      id: 'employees-managers',
      title: 'Employees & Managers',
      description: `Manage ${companySettings.totalEmployees} staff members`,
      icon: <People />,
      action: () => navigate(ROUTES.USERS),
    },
    {
      id: 'custom-fields',
      title: 'Custom Fields',
      description: 'Create custom staff information fields',
      icon: <Settings />,
      action: () => navigate('/settings/custom-fields'),
    },
    {
      id: 'inactive-employees',
      title: 'Inactive Employees',
      description: `Manage ${companySettings.inactiveEmployees} inactive employees`,
      icon: <People />,
      action: () => navigate('/users?status=inactive'),
    },
  ];

  const attendanceSettingsItems: SettingsItem[] = [
    {
      id: 'attendance-modes',
      title: 'Attendance Modes',
      description: 'Configure attendance tracking methods',
      icon: <AccessTime />,
      action: () => navigate('/settings/attendance-modes'),
    },
    {
      id: 'biometric',
      title: 'Biometric Devices',
      description: 'Add biometric device and manage employees',
      icon: <Fingerprint />,
      action: () => navigate('/settings/biometric'),
    },
    {
      id: 'leaves-holidays',
      title: 'Leaves & Holidays',
      description: 'Manage leave policies and holiday calendar',
      icon: <CalendarToday />,
      action: () => navigate('/settings/leaves-holidays'),
    },
  ];

  const systemSettingsItems: SettingsItem[] = [
    {
      id: 'security',
      title: 'Security Settings',
      description: 'Manage security policies and access controls',
      icon: <Security />,
      action: () => navigate('/settings/security'),
    },
    {
      id: 'notifications',
      title: 'Notification Settings',
      description: 'Configure system notifications and alerts',
      icon: <Notifications />,
      action: () => navigate('/settings/notifications'),
    },
    {
      id: 'storage-cleanup',
      title: 'Storage Cleanup',
      description: 'Manage photo cleanup and S3 storage optimization',
      icon: <Storage />,
      action: handleOpenStorageCleanup,
    },
    {
      id: 'backup',
      title: 'Data Backup',
      description: 'Manage data backup and recovery',
      icon: <Backup />,
      action: () => navigate('/settings/backup'),
    },
    {
      id: 'system-updates',
      title: 'System Updates',
      description: 'Check for system updates and maintenance',
      icon: <SystemUpdate />,
      action: () => navigate('/settings/updates'),
    },
  ];

  const otherSettingsItems: SettingsItem[] = [
    {
      id: 'help-support',
      title: 'Help & Support',
      description: 'Get help and contact support',
      icon: <Help />,
      action: () => navigate('/help'),
    },
    {
      id: 'about',
      title: 'About Triveni Admin',
      description: 'Version information and system details',
      icon: <Info />,
      action: () => navigate('/about'),
    },
    {
      id: 'logout',
      title: 'Logout',
      description: 'Logout from the system',
      icon: <Logout />,
      action: () => setLogoutDialog(true),
    },
  ];

  const renderSettingsSection = (title: string, items: SettingsItem[]) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <List disablePadding>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={item.action}
                  disabled={item.disabled}
                  sx={{
                    py: 2,
                    px: 3,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#6c757d', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#081122' }}>
                          {item.title}
                        </Typography>
                        {item.badge && (
                          <Chip
                            label={item.badge}
                            size="small"
                            color="error"
                            sx={{ fontSize: '0.75rem', height: 20 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: '#666666', mt: 0.5 }}>
                        {item.description}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small">
                      <Edit />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
              {index < items.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: '#6c757d' }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600 }}>
          Settings
        </Typography>
      </Box>

      {/* User Info Card */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <ProfileAvatar
            imageUrl={user?.profile_image_url}
            firstName={user?.first_name}
            lastName={user?.last_name}
            sx={{ bgcolor: '#6c757d', width: 60, height: 60, fontSize: '1.5rem' }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
              {user?.role?.replace('_', ' ').toUpperCase()} • Employee ID: {user?.employee_id}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              Last Login: {user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
            </Typography>
          </Box>
          <Chip
            label={user?.is_active ? 'Active' : 'Inactive'}
            color={user?.is_active ? 'success' : 'error'}
            size="small"
          />
        </Box>
      </Paper>

      {/* Settings Sections */}
      {renderSettingsSection('Company Details', companySettingsItems)}
      {renderSettingsSection('My Team', teamSettingsItems)}
      {renderSettingsSection('Attendance Settings', attendanceSettingsItems)}
      {renderSettingsSection('System Settings', systemSettingsItems)}
      {renderSettingsSection('Other Settings', otherSettingsItems)}

      {/* Edit Company Dialog */}
      <Dialog open={editCompanyDialog} onClose={() => setEditCompanyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Company Details</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Company Name"
            value={companyForm.name}
            onChange={(e) => setCompanyForm(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Company Code"
            value={companyForm.code}
            onChange={(e) => setCompanyForm(prev => ({ ...prev, code: e.target.value }))}
            margin="normal"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCompanyDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCompany} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout from the system?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)}>Cancel</Button>
          <Button onClick={handleLogout} variant="contained" color="error">Logout</Button>
        </DialogActions>
      </Dialog>

      {/* Storage Cleanup Dialog */}
      <Dialog 
        open={storageCleanupDialog} 
        onClose={() => !cleanupRunning && setStorageCleanupDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Storage sx={{ color: '#081122' }} />
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Storage Cleanup
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {storageCleanupSettings && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Photos older than {storageCleanupSettings.photoRetentionDays} days are automatically deleted to optimize S3 storage costs.
                The cleanup job runs daily at 2:00 AM IST.
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#666666', mb: 1 }}>
                        Retention Period
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                        {storageCleanupSettings.photoRetentionDays} Days
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#666666', mb: 1 }}>
                        Schedule
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                        {storageCleanupSettings.cleanupSchedule}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
                  Photos Cleaned Up (Deleted after {storageCleanupSettings.photoRetentionDays} days):
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {storageCleanupSettings.photosCleanedUp.map((photo, index) => (
                    <Typography key={index} variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                      • {photo}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
                  Photos Retained (Never deleted):
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {storageCleanupSettings.photosRetained.map((photo, index) => (
                    <Typography key={index} variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                      • {photo}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {storageCleanupSettings.lastCleanupRun && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Last cleanup run: {new Date(storageCleanupSettings.lastCleanupRun).toLocaleString()}
                </Alert>
              )}

              {cleanupResult && (
                <Card sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                      Cleanup Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666666' }}>Attendance Photos</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {cleanupResult.stats.attendance.deleted} deleted
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666666' }}>Temperature Captures</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {cleanupResult.stats.temperatureCapture.deleted} deleted
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666666' }}>Vehicle Captures</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {cleanupResult.stats.vehicleCapture.deleted} deleted
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: '#666666' }}>Security Logs</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {cleanupResult.stats.securityLog.deleted} deleted
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ color: '#666666' }}>Total S3 Files Deleted</Typography>
                        <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                          {cleanupResult.stats.totalS3Deletions}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ color: '#666666', mt: 1, display: 'block' }}>
                      Completed at: {new Date(cleanupResult.completedAt).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setStorageCleanupDialog(false)} 
            disabled={cleanupRunning}
          >
            Close
          </Button>
          <Button
            onClick={handleRunCleanup}
            variant="contained"
            startIcon={cleanupRunning ? <CircularProgress size={20} /> : <Delete />}
            disabled={cleanupRunning}
            sx={{
              background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
              },
            }}
          >
            {cleanupRunning ? 'Running Cleanup...' : 'Run Cleanup Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
